import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { ArrowLeft, Phone, MessageSquare, Clock, MapPin, User } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useData } from '../contexts/DataContext';

// Fix default marker icons (leaflet + webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const engineerIcon = new L.DivIcon({
  className: '',
  html: '<div style="width:44px;height:44px;border-radius:50%;background:#FF5C39;border:4px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:11px;">DHS</div>',
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

const homeIcon = new L.DivIcon({
  className: '',
  html: '<div style="width:36px;height:36px;border-radius:50%;background:#1F0068;border:4px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;color:#fff;">🏠</div>',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length < 2) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [60, 60] });
  }, [map, points]);
  return null;
};

const distanceKm = (a, b) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
};

const TrackEngineer = () => {
  const { id } = useParams();
  const { claims, updateEngineerLocation } = useData();
  const nav = useNavigate();
  const claim = claims.find((c) => c.id === id);

  const [eng, setEng] = useState(claim?.engineerLocation || null);
  const home = claim?.customerLocation;

  useEffect(() => {
    if (!claim || !claim.customerLocation || !claim.engineerLocation) return;
    const t = setInterval(() => {
      setEng((cur) => {
        if (!cur) return cur;
        const dLat = home.lat - cur.lat;
        const dLng = home.lng - cur.lng;
        const d = Math.sqrt(dLat * dLat + dLng * dLng);
        if (d < 0.0005) return cur; // arrived
        const step = 0.06; // move 6% closer per tick
        const next = { lat: cur.lat + dLat * step, lng: cur.lng + dLng * step };
        updateEngineerLocation(claim.id, next);
        return next;
      });
    }, 2000);
    return () => clearInterval(t);
  }, [claim, home, updateEngineerLocation]);

  const distance = useMemo(() => (eng && home ? distanceKm(eng, home) : 0), [eng, home]);
  const etaMin = Math.max(1, Math.round(distance / 30 * 60)); // assume 30 km/h avg

  if (!claim) {
    return (
      <div className="bg-dhs min-h-screen">
        <Header />
        <main className="py-20 text-center">
          <p className="text-[#1F0068] font-bold">Claim not found.</p>
          <Button className="mt-4 rounded-full" onClick={() => nav('/dashboard')}>Back to dashboard</Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main className="py-6 md:py-10">
        <div className="max-w-7xl mx-auto px-5">
          <Link to={`/dashboard/claim/${claim.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F0068]/70 hover:text-[#1F0068] mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to claim
          </Link>

          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="bg-white rounded-3xl border border-[#1F0068]/5 overflow-hidden shadow-lg">
              <div className="h-[560px] w-full">
                {home && eng && (
                  <MapContainer center={[eng.lat, eng.lng]} zoom={13} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[home.lat, home.lng]} icon={homeIcon}><Popup>Your home</Popup></Marker>
                    <Marker position={[eng.lat, eng.lng]} icon={engineerIcon}><Popup><b>{claim.engineer || 'Engineer'}</b><br/>On the way</Popup></Marker>
                    <Polyline positions={[[eng.lat, eng.lng], [home.lat, home.lng]]} pathOptions={{ color: '#FF5C39', weight: 4, dashArray: '8 8', opacity: 0.7 }} />
                    <FitBounds points={[[home.lat, home.lng], [eng.lat, eng.lng]]} />
                  </MapContainer>
                )}
              </div>
            </div>

            <aside className="space-y-4">
              <div className="bg-[#1F0068] text-white rounded-3xl p-6">
                <div className="text-xs uppercase tracking-widest text-white/70 font-bold">Engineer’s ETA</div>
                <div className="mt-1 text-5xl font-extrabold">{etaMin} <span className="text-lg font-medium text-white/70">min</span></div>
                <div className="mt-1 text-sm text-white/80">{distance.toFixed(1)} km away</div>
                <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#FF5C39] rounded-full transition-all duration-1000" style={{ width: `${Math.min(100, Math.max(6, 100 - distance * 8))}%` }} />
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#1F0068]/5 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#FFEDD6] text-[#1F0068] flex items-center justify-center font-bold"><User className="w-5 h-5" /></div>
                  <div>
                    <div className="font-bold text-[#1F0068]">{claim.engineer || 'Assigning…'}</div>
                    <div className="text-xs text-[#1F0068]/60">Domestic Home Services engineer</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <a href="tel:01135487557"><Button variant="outline" className="w-full rounded-full border-[#1F0068] text-[#1F0068] hover:bg-[#1F0068] hover:text-white gap-1.5 h-11"><Phone className="w-4 h-4" /> Call</Button></a>
                  <Link to={`/dashboard/claim/${claim.id}`}><Button variant="outline" className="w-full rounded-full border-[#1F0068] text-[#1F0068] hover:bg-[#1F0068] hover:text-white gap-1.5 h-11"><MessageSquare className="w-4 h-4" /> Chat</Button></Link>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#1F0068]/5 p-5">
                <div className="text-xs uppercase tracking-widest text-[#1F0068]/60 font-bold mb-3">Job details</div>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-[#1F0068]/60">Reference</dt><dd className="font-bold text-[#1F0068]">{claim.id}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#1F0068]/60">Appliance</dt><dd className="font-semibold text-[#1F0068]">{claim.appliance}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#1F0068]/60">Status</dt><dd className="font-semibold text-[#FF5C39]">{claim.status}</dd></div>
                  {claim.scheduled && (
                    <div className="flex justify-between"><dt className="text-[#1F0068]/60">Slot</dt><dd className="font-semibold text-[#1F0068]">{new Date(claim.scheduled).toLocaleDateString('en-GB')}</dd></div>
                  )}
                </dl>
              </div>

              <div className="bg-[#FFEDD6] rounded-2xl p-4 text-xs text-[#1F0068]/80">
                <div className="flex items-center gap-2 font-semibold text-[#1F0068] mb-1"><Clock className="w-3.5 h-3.5" /> Live tracking</div>
                Location updates in real time. Your engineer will call you 15 minutes before arriving.
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TrackEngineer;
